// src/components/chat/ChatList.jsx
// Left-sidebar conversation list — renders unified conversations from the
// backend API merged with optimistic / archived threads and live Matrix data.
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import useMatrixClient from '../../hooks/useMatrixClient';
import { useMessagingStore } from '../../state/messagingStore';
import { useMatrixStore } from '../../state/matrixStore';
import useOtherUsersStore from '../../state/otherUsersStore';
import { useConversationStore } from '../../state/conversationStore';
import useTeamStore from '../../state/teamStore';
import { useProfileStore } from '../../state/profileStore';
import PresenceAvatar from '../shared/PresenceAvatar';

import {
  initialsOf,
  tinyWhen,
  inviteTimestamp,
  peerUidFromRoom,
  peerAvatarHttp,
  roomDisplay,
  isStrictDM,
  lastText,
} from './chatHelpers';

export default function ChatList() {
  const { client: mxClient, isReady: mxReady } = useMatrixClient();
  const selectedThread = useMessagingStore(s => s.selectedThread);
  const setSelectedThread = useMessagingStore(s => s.setSelectedThread);
  const optimisticThreads = useMessagingStore(s => s.optimisticThreads);
  const archivedThreads = useMessagingStore(s => s.archivedThreads || []);
  const reconcileDM = useMessagingStore(s => s.reconcileDM);
  const getOtherUser = useOtherUsersStore(s => s.getUser);
  // Captain gate + persisted invite draft (teamStore is already persisted)
  const myTeams = useTeamStore((s) => s.myTeams || []);
  const setInviteDraft = useTeamStore((s) => s.setInviteDraft);
  const ensureMyTeamsHydrated = useTeamStore((s) => s.ensureMyTeamsHydrated);

  const viewerUId = useProfileStore((s) => Number(s?.profile?.u_id) || null);

  // Hydrate teams for captain gating (works even when eh_team_v1 is empty after cache clear)
  useEffect(() => {
    if (!viewerUId) return;
    ensureMyTeamsHydrated(viewerUId);
  }, [viewerUId, ensureMyTeamsHydrated]);


  const captainTeamsCount = useMemo(() => {
    const uid = Number(viewerUId || 0);
    if (!uid) return 0;

    return (myTeams || []).filter((t) => {
      const role = String(t?.role || '').toLowerCase();
      const ownerId = Number(t?.owner_u_id || 0);
      const captainId = Number(t?.captain_u_id || 0);

      return role === 'captain' || ownerId === uid || captainId === uid;
    }).length;
  }, [myTeams, viewerUId]);

  const canInviteToTeam = captainTeamsCount > 0;


  const setSelectedRoomId = useMatrixStore(s => s.setSelectedRoomId);
  const unreadCounts = useMatrixStore(s => s.unreadCounts);
  const clearUnreadCount = useMatrixStore(s => s.clearUnreadCount);
  const markRoomRead = useMatrixStore(s => s.markRoomRead);
  const skipAutoSelect = useMatrixStore(s => s.skipAutoSelect);
  const setSkipAutoSelect = useMatrixStore(s => s.setSkipAutoSelect);

  const [showArchived, setShowArchived] = useState(false);
  const activeItemRef = useRef(null);

  const conversations = useConversationStore(s => s.conversations);


  // When Matrix reports a new room (or we join one), reconcile the optimistic row by alias
  useEffect(() => {
    if (!mxReady || !mxClient) return;

    const onRoom = (room) => {
      try {
        const aliasRaw =
          room.getCanonicalAlias?.() ||
          (room.getAliases?.()?.[0] || null);

        if (!aliasRaw) {
          return;
        }

        const alias = String(aliasRaw);
        const roomId = room.roomId;

        // 1) Remove any optimistic thread that uses this alias
        //    (this drops the placeholder card from ChatList).
        reconcileDM({ alias, roomId });

        // 2) If the *currently selected* thread is this optimistic alias,
        //    immediately switch the selection to the real Matrix room so
        //    ChatView renders the live message UI instead of invite mode.
        const state = useMessagingStore.getState();
        const currentSelected = state.selectedThread;

        // selectedThread may be the alias itself (e.g. "#eh.dm.u1_u2:…")
        // or an explicit "opt:<alias>" id if we ever use that pattern.
        const isCurrentAlias =
          currentSelected &&
          (String(currentSelected) === alias ||
           String(currentSelected) === `opt:${alias}`);

if (isCurrentAlias && roomId) {


  // 1) Switch selection so ChatView stops showing optimistic UI.
  setSelectedThread(roomId);
  setSelectedRoomId(roomId);

  // 2) Kill stale optimistic record (drop "sent successfully" UI)
  useMessagingStore.setState((state) => ({
    optimisticThreads: (state.optimisticThreads || []).filter(
      (t) => String(t.alias) !== alias
    ),
  }));
}

      } catch (e) {
        // no-op
      }
    };

    mxClient.on('Room', onRoom);
    return () => {
      mxClient.removeListener('Room', onRoom);
    };
  }, [mxReady, mxClient, reconcileDM, setSelectedThread, setSelectedRoomId]);



  const [version, setVersion] = useState(0);

  // bump on room/timeline/state/sync so list stays live
  useEffect(() => {
    if (!mxClient) return;
    const bump = () => setVersion((v) => v + 1);
    mxClient.on('Room', bump);
    mxClient.on('Room.timeline', bump);
    mxClient.on('RoomState.events', bump);
    mxClient.on('sync', bump);
    return () => {
      mxClient.removeListener('Room', bump);
      mxClient.removeListener('Room.timeline', bump);
      mxClient.removeListener('RoomState.events', bump);
      mxClient.removeListener('sync', bump);
    };
  }, [mxClient]);

  // On mount (and when Matrix becomes ready), reconcile any existing rooms
  // with optimistic threads + selection. This covers the case where the DM
  // room was created while ChatCenter was closed or in the background.
  useEffect(() => {
    if (!mxReady || !mxClient) return;

    const state = useMessagingStore.getState();
    const optimisticThreads = state.optimisticThreads || [];
    const currentSelected = state.selectedThread;

    if (!optimisticThreads.length && !currentSelected) return;

    const allRooms = mxClient.getRooms?.() || [];

    allRooms.forEach((room) => {
      const aliasRaw =
        room.getCanonicalAlias?.() ||
        (room.getAliases?.()?.[0] || null);

      if (!aliasRaw) return;

      const alias = String(aliasRaw);
      const roomId = room.roomId;
      if (!roomId) return;

      // Inform messagingStore that this alias now has a real Matrix room
      reconcileDM({ alias, roomId });

      const isSelectedAlias =
        currentSelected &&
        (String(currentSelected) === alias ||
         String(currentSelected) === `opt:${alias}`);

      if (isSelectedAlias) {


        // Upgrade selection so ChatView shows the Matrix chat UI
        setSelectedThread(roomId);
        setSelectedRoomId(roomId);

        // Drop the stale optimistic record so we do not keep showing the
        // "Chat request sent successfully" UI for this alias.
        useMessagingStore.setState((prev) => ({
          optimisticThreads: (prev.optimisticThreads || []).filter(
            (t) => String(t.alias) !== alias
          ),
        }));
      }
    });
  }, [mxReady, mxClient, reconcileDM, setSelectedThread, setSelectedRoomId]);

  const items = useMemo(() => {

    // If Matrix isn't ready, don't show any Matrix rooms yet
    if (!mxReady || !mxClient) return [];

    const myUserId = mxClient.getUserId?.() || '';
    const allRooms = mxClient.getRooms?.() || [];

    // Keep only strict 1:1 DMs where I'm joined or invited
    const dmRooms = allRooms.filter((room) => {
      if (!isStrictDM(room)) return false;

      const me = room.getMember?.(myUserId);
      const membership = String(me?.membership || '').toLowerCase();
      return membership === 'join' || membership === 'invite';
    });

    if (!dmRooms.length) return [];

    const mapped = dmRooms.map((room) => {
      const myMember = room.getMember?.(myUserId);
      const membershipRaw = String(myMember?.membership || 'join').toLowerCase();
      const membership = membershipRaw === 'invite' ? 'invite' : 'join';

      const { text, ts, tone } = lastText(room);

      const alias =
        room.getCanonicalAlias?.() ||
        ((room.getAliases?.() || [])[0] || null);

      const peerUid = peerUidFromRoom(room, myUserId);
      const avatarUrl = peerAvatarHttp(room, mxClient, myUserId);

      const inviteTs = inviteTimestamp(room, myUserId);
      const lastActive =
        membership === 'invite'
          ? (inviteTs || ts || 0)
          : (ts || inviteTs || 0);

      const preview =
        membership === 'invite'
          ? 'Chat Request Pending'
          : (text || 'No messages yet');

      return {
        id: room.roomId,
        joinable: alias || room.roomId,
        alias,
        membership,
        title: roomDisplay(room, myUserId),
        avatarUrl,
        preview,
        previewTone: tone,
        previewWhen: tinyWhen(lastActive),
        lastActive,
        peerUid,
      };

    });

    // Sort: invites first (newest), then joined (newest)
    const byNewest = (a, b) => (b.lastActive || 0) - (a.lastActive || 0);
    const invites = mapped.filter((m) => m.membership === 'invite').sort(byNewest);
    const joined  = mapped.filter((m) => m.membership === 'join').sort(byNewest);

    return [...invites, ...joined];
  }, [mxClient, mxReady, version]);


  // Optimistic rows come from the store (created by openWithCandidate).
  // They render instantly with real name + profile photo from otherUsersStore.
  const optimisticItems = useMemo(() => {
    if (!optimisticThreads || !optimisticThreads.length) return [];

    return optimisticThreads
      .map((t) => {
        const peerUidNum = Number(t.peerUid);

        const otherUser =
          Number.isFinite(peerUidNum) && typeof getOtherUser === 'function'
            ? getOtherUser(peerUidNum)
            : null;



        const title =
          t.displayName ||
          (otherUser && (otherUser.displayName || otherUser.name)) ||
          (peerUidNum ? `User ${peerUidNum}` : 'User');

        // We treat "avatar" as the user profile photo coming from otherUsersStore.
        const avatarUrl =
          (otherUser &&
            (otherUser.avatar ||
             otherUser.profilePhotoUrl ||
             otherUser.photoUrl)) ||
          t.avatarUrl ||
          null;

        return {
          id: `opt:${t.alias}`,
          joinable: t.alias, // we select by alias
          alias: t.alias,
          membership: 'optimistic',
          status: t.status || 'pending', // carry through invite status (pending / accepted / declined)
          title,
          avatarUrl,
          preview: t.lastMessagePreview || 'Start a conversation…',
          previewWhen: tinyWhen(t.lastMessageAt || Date.now()),
          lastActive: Number(t.lastMessageAt || Date.now()),
          peerUid: t.peerUid,
          isOptimistic: true,
        };

      })
      // newest optimistic at top
      .sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
  }, [optimisticThreads, getOtherUser]);






  const listHasSelected = useMemo(() => {
    if (!selectedThread) return false;
    const hitReal = items.some(it =>
      String(it.joinable) === String(selectedThread) ||
      String(it.id) === String(selectedThread)
    );
    if (hitReal) return true;
    const hitOpt = optimisticItems.some(it =>
      String(it.joinable) === String(selectedThread) ||
      String(it.id) === String(selectedThread)
    );
    return hitOpt;
  }, [items, optimisticItems, selectedThread]);

  // Whenever the selected thread changes, scroll its card into view so the
  // user can see which conversation is focused (even if it's further down).
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedThread]);

  const onSelect = (it) => {
    // User manually selected a conversation, so clear the skipAutoSelect flag
    // so that future opens can auto-select if appropriate
    if (skipAutoSelect) {
      setSkipAutoSelect(false);
    }

    // never auto-join here; ChatView handles Accept/Decline
    setSelectedThread(it.joinable);

    // For real Matrix rooms (joined or invite), wire ChatView to that room.
    // For optimistic / archived threads, clear the Matrix selection so
    // ChatView stays in the invite / archived UI.
    if (it.membership === 'optimistic' || it.membership === 'archived') {
      setSelectedRoomId(null);
    } else if (it.id) {
      setSelectedRoomId(it.id);
      clearUnreadCount(it.id);
      // Persist read state so it survives page refresh
      const myMxid = mxClient?.getUserId?.() || '';
      if (myMxid) {
        markRoomRead(myMxid, it.id, Date.now());
      }
    }
  };




  // Prefer the real Matrix room if it exists; otherwise show the optimistic row.
  // Track real aliases we've seen (when available)…
  const realAliases = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (it?.alias) set.add(String(it.alias));
    }
    return set;
  }, [items]);

  // …and ALSO track real peer UIDs (fallback when alias isn't populated yet).
  const realPeerUids = useMemo(() => {
    const set = new Set();
    for (const it of items) {
      if (Number.isFinite(it?.peerUid)) set.add(Number(it.peerUid));
    }
    return set;
  }, [items]);

const mergedItems = useMemo(() => {
    // Do not show threads in the active list if they have been archived locally
    const archivedAliasSet = new Set(
      (archivedThreads || []).map((t) => String(t.alias || ''))
    );

    // keep only optimistic rows that don't yet have a real room
    // primary: drop if a real room with SAME alias exists
    // fallback: drop if a real room with SAME peerUid exists (alias not ready yet)
    const onlyOptimistic = optimisticItems.filter((o) => {
      const aliasStr = String(o.alias || '');
      const aliased = aliasStr && realAliases.has(aliasStr);
      const peerHit =
        Number.isFinite(o.peerUid) && realPeerUids.has(Number(o.peerUid));
      const isArchived = archivedAliasSet.has(aliasStr);

      // Drop if there's a real room or the thread has been archived.
      // IMPORTANT: accepted status alone does NOT drop the optimistic card;
      // we keep showing it until a Matrix room actually exists.
      return !(aliased || peerHit || isArchived);
    });

    return [...onlyOptimistic, ...items];

  }, [optimisticItems, items, realAliases, realPeerUids, archivedThreads]);

  // When a Matrix DM room appears for the SAME alias we previously had
  // selected (the optimistic invite), upgrade selection from the alias
  // to the real roomId. This keeps the focus on the *same user* and
  // switches ChatView out of "Invite Sent" mode into normal chat.
  useEffect(() => {
    if (!selectedThread) return;

    // We only care about the case where selectedThread is still the alias,
    // but the list now has a real room row for that alias.
    const match = mergedItems.find((it) => {
      if (!it.alias) return false;
      const aliasStr = String(it.alias);
      const selStr   = String(selectedThread);

      // selectedThread is the alias (#eh.dm.uX_uY:domain)
      // but joinable/id are now the Matrix roomId.
      const isSameAlias = aliasStr === selStr;
      const isAlreadyRoom =
        selStr === String(it.joinable) || selStr === String(it.id);

      return isSameAlias && !isAlreadyRoom;
    });

    if (!match) return;



    // Update messagingStore selection to point at the real Matrix room
    setSelectedThread(match.joinable);

    // And tell matrixStore which room ChatView should bind to
    if (match.membership === 'optimistic' || match.membership === 'archived') {
      setSelectedRoomId(null);
    } else if (match.id) {
      setSelectedRoomId(match.id);
    }
  }, [mergedItems, selectedThread, setSelectedThread, setSelectedRoomId]);

  // If nothing is selected but we have optimistic threads AND a real Matrix DM
  // room for one of those same users, refocus to that specific conversation.
  // This covers your case: invite was sent, then accepted, and a Matrix room
  // exists, but selectedThread was cleared during refresh / reopen.
  // NOTE: Skip auto-selection if user intentionally opened with no selection (from docking station)
  useEffect(() => {
    // Only run when nothing is selected
    if (selectedThread) return;
    if (!items.length) return;
    if (!optimisticThreads || !optimisticThreads.length) return;

    // If user intentionally opened ChatCenter with no selection, don't auto-select
    if (skipAutoSelect) return;

    let chosenThread = null;
    let match = null;

    for (const t of optimisticThreads) {
      if (!t) continue;

      const aliasStr = String(t.alias || '');
      const peerUidNum = Number(t.peerUid);

      // Find the real Matrix DM item for this same user:
      //  - Prefer exact alias match when available
      //  - Fallback to peerUid match if alias isn't populated
      const found = items.find((it) => {
        if (!it) return false;
        if (it.membership !== 'join') return false; // only fully joined DMs

        if (it.alias && aliasStr && String(it.alias) === aliasStr) {
          return true;
        }

        if (
          Number.isFinite(peerUidNum) &&
          Number.isFinite(it.peerUid) &&
          Number(it.peerUid) === peerUidNum
        ) {
          return true;
        }

        return false;
      });

      if (found) {
        chosenThread = t;
        match = found;
        break;
      }
    }

    if (!match) return;



    // Refocus: behave exactly as if the user clicked this row.
    setSelectedThread(match.joinable);

    if (match.membership === 'optimistic' || match.membership === 'archived') {
      setSelectedRoomId(null);
    } else if (match.id) {
      setSelectedRoomId(match.id);
    }
  }, [items, optimisticThreads, selectedThread, setSelectedThread, setSelectedRoomId, skipAutoSelect]);

// Unified conversations from /api/conversations (for transition)
  const pendingIncoming = useMemo(
    () => (conversations || []).filter(
      c => c.conversation_type === 'pending_invite_incoming'
    ),
    [conversations]
  );

  const pendingOutgoing = useMemo(
    () => (conversations || []).filter(
      c => c.conversation_type === 'pending_invite_outgoing'
    ),
    [conversations]
  );

  const activeDMs = useMemo(
    () => (conversations || []).filter(
      c => c.conversation_type === 'active_matrix_dm'
    ),
    [conversations]
  );

  // Unified conversations sorted by most recent activity (backend sort_bucket ignored)
  const unifiedConversationsSorted = useMemo(() => {
    const base = (conversations || []).slice();

    // Cache room->lastTs so we don't re-scan timelines repeatedly during sort.
    const roomLastTsCache = new Map();

    const getRoomLastTs = (roomId) => {
      const key = String(roomId || '');
      if (!key) return 0;

      if (roomLastTsCache.has(key)) return roomLastTsCache.get(key);

      let ts = 0;
      try {
        if (mxReady && mxClient) {
          const room = mxClient.getRoom?.(key);
          const evs = room?.getLiveTimeline?.()?.getEvents?.() || [];
          const last = evs.length ? evs[evs.length - 1] : null;
          ts = Number(last?.getTs?.() || 0) || 0;
        }
      } catch {
        ts = 0;
      }

      roomLastTsCache.set(key, ts);
      return ts;
    };

    const effectiveTs = (conv) => {
      const apiTs = conv?.sort_date ? Date.parse(conv.sort_date) : 0;
      const mxTs = conv?.room_id ? getRoomLastTs(conv.room_id) : 0;
      return Math.max(apiTs || 0, mxTs || 0);
    };

    return base.sort((a, b) => effectiveTs(b) - effectiveTs(a));
  }, [conversations, mxClient, mxReady, version]);


  // Bridge: include optimistic threads as synthetic unified rows
  // so a brand new invite appears immediately, even before
  // /api/conversations has been refreshed.
  const unifiedConversationsWithOptimistic = useMemo(() => {
    const baseRaw = unifiedConversationsSorted || [];

    // Any peer that has been archived locally (declined invite, etc.)
    // should NOT show a pending invite row in the active list.
    const suppressedPeerIds = new Set(
      (archivedThreads || [])
        .map((t) => {
          const id = Number(t.peerUid);
          return Number.isFinite(id) ? id : null;
        })
        .filter((id) => id !== null)
    );

    // STEP 1: if there is an active_matrix_dm row for a given peer,
    // drop any pending/other rows for that same other_user_id.
    const peerHasActive = new Set(
      baseRaw
        .filter((c) => c.conversation_type === 'active_matrix_dm')
        .map((c) => {
          const id = Number(c.other_user_id || 0);
          return Number.isFinite(id) ? id : null;
        })
        .filter((id) => id !== null)
    );

    const base = baseRaw.filter((c) => {
      const peerId = Number(c.other_user_id || 0);
      if (!Number.isFinite(peerId)) return true;

      // If this peer has been archived locally, hide any
      // pending-invite rows from the active list.
      if (
        suppressedPeerIds.has(peerId) &&
        (c.conversation_type === 'pending_invite_incoming' ||
          c.conversation_type === 'pending_invite_outgoing' ||
          c.conversation_type === 'declined_dm')
      ) {
        return false;
      }

      // If there is no active DM for this peer, keep everything.
      if (!peerHasActive.has(peerId)) return true;

      // If there *is* an active DM, keep only the active row(s);
      // drop stale pending/declined rows for this peer.
      return c.conversation_type === 'active_matrix_dm';
    });

    // If we have no optimistic items, we are done after dedupe.
    if (!optimisticItems || !optimisticItems.length) {
      return base;
    }

    // STEP 2: Track which peer ids are already represented in the
    // deduped backend list, so we only synthesize optimistic rows
    // for peers that do NOT exist in the backend yet.
    const coveredPeerIds = new Set(
      base
        .map((c) => {
          const id = Number(c.other_user_id || 0);
          return Number.isFinite(id) ? id : null;
        })
        .filter((id) => id !== null)
    );

    // For each optimistic thread whose peer is not yet covered,
    // synthesize a unified-style conversation row.
    const syntheticFromOptimistic = optimisticItems
      .filter((opt) => {
        const peerId = Number(opt.peerUid);
        return Number.isFinite(peerId) && !coveredPeerIds.has(peerId);
      })
      .map((opt) => {
        const peerId = Number(opt.peerUid);
        const ts = Number(opt.lastActive || Date.now());

        return {
          id: `opt:${opt.id}`,
          other_user_id: Number.isFinite(peerId) ? peerId : null,
          other_user_display_name: opt.title,
          other_user_avatar_url: opt.avatarUrl || null,
          conversation_type: 'pending_invite_outgoing',
          status: 'pending',
          sort_date: new Date(ts).toISOString(),
          room_id: null,
        };
      });

    const merged = [...syntheticFromOptimistic, ...base];

    return merged.sort((a, b) => {
      const ta = a.sort_date ? Date.parse(a.sort_date) : 0;
      const tb = b.sort_date ? Date.parse(b.sort_date) : 0;
      return tb - ta; // newest first
    });
  }, [unifiedConversationsSorted, optimisticItems, archivedThreads]);




  const archivedItems = useMemo(() => {
    if (!archivedThreads || !archivedThreads.length) return [];

    return archivedThreads
      .map((t) => {
        const peerUidNum = Number(t.peerUid);

        const otherUser =
          Number.isFinite(peerUidNum) && typeof getOtherUser === 'function'
            ? getOtherUser(peerUidNum)
            : null;

        const title =
          t.displayName ||
          (otherUser && (otherUser.displayName || otherUser.name)) ||
          (peerUidNum ? `User ${peerUidNum}` : 'User');

        const avatarUrl =
          (otherUser &&
            (otherUser.avatar ||
             otherUser.profilePhotoUrl ||
             otherUser.photoUrl)) ||
          t.avatarUrl ||
          null;

        return {
          id: `arch:${t.alias}`,
          joinable: t.alias,
          alias: t.alias,
          membership: 'archived',
          title,
          avatarUrl,
          preview: t.lastMessagePreview || 'Invitation declined',
          previewWhen: tinyWhen(t.lastMessageAt || Date.now()),
          lastActive: Number(t.lastMessageAt || Date.now()),
          peerUid: t.peerUid,
          isOptimistic: true,
        };
      })
      .sort((a, b) => (b.lastActive || 0) - (a.lastActive || 0));
  }, [archivedThreads, getOtherUser]);


  // Pre-compute live timestamps for sorting — avoids expensive room lookups
  // inside the sort comparator (O(n log n) → O(n) for the expensive part).
  const sortedConversations = useMemo(() => {
    const list = unifiedConversationsWithOptimistic || [];
    if (!list.length) return list;

    // Build an alias→room index once so we don't call getRooms() per-item
    const aliasRoomMap = new Map();
    if (mxClient?.getRooms) {
      for (const r of mxClient.getRooms()) {
        const ca = r.getCanonicalAlias?.();
        if (ca) aliasRoomMap.set(ca, r);
      }
    }

    // Build a peerUid→lastActive index from mergedItems
    const peerTsMap = new Map();
    for (const it of mergedItems) {
      if (Number.isFinite(it?.peerUid)) {
        peerTsMap.set(Number(it.peerUid), Number(it.lastActive) || 0);
      }
    }

    const myUid = Number(viewerUId || 0);

    const getLiveTs = (conv) => {
      // 1) Room lookup by room_id
      if (conv?.room_id && mxClient?.getRoom) {
        const room = mxClient.getRoom(conv.room_id);
        if (room) {
          const ts = Number(lastText(room)?.ts || 0) || 0;
          if (ts) return ts;
        }
      }

      // 2) mergedItems lookup by peerUid
      const peerId = Number(conv?.other_user_id || 0);
      if (Number.isFinite(peerId) && peerTsMap.has(peerId)) {
        const ts = peerTsMap.get(peerId);
        if (ts) return ts;
      }

      // 3) Alias-based room lookup for DMs excluded by isStrictDM
      if (Number.isFinite(peerId) && peerId > 0 && myUid) {
        const a = Math.min(myUid, peerId);
        const b = Math.max(myUid, peerId);
        const alias = `#eh.dm.u${a}_u${b}:ehandshake.com`;
        const dmRoom = aliasRoomMap.get(alias);
        if (dmRoom) {
          const ts = Number(lastText(dmRoom)?.ts || 0) || 0;
          if (ts) return ts;
        }
      }

      // 4) Fallback: backend sort_date
      return conv?.sort_date ? Date.parse(conv.sort_date) : 0;
    };

    // Pre-compute timestamps, then sort
    const withTs = list.map((conv) => ({ conv, ts: getLiveTs(conv) }));
    withTs.sort((a, b) => b.ts - a.ts);
    return withTs.map((entry) => entry.conv);
  }, [unifiedConversationsWithOptimistic, mergedItems, mxClient, viewerUId, version]);

  return (
    <div className="h-full overflow-y-auto overscroll-contain relative scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-100">
      <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-16 sm:pb-16">

        {/* Unified conversations list */}
        {sortedConversations.length > 0 && (
          <div className="mb-3 border-b border-gray-200 pb-2">

            <ul className="space-y-1">
              {sortedConversations.map((conv) => {

                const title =
                  conv.other_user_display_name ||
                  (conv.other_user_id ? `User ${conv.other_user_id}` : 'User');

                const avatarUrl = conv.other_user_avatar_url || null;

// Choose the preview text based on status + conversation_type
                let preview = 'No messages yet';

                // Only true pending invites show "Chat Request Pending"
                // or "Incoming chat request". Once the conversation
                // is an active DM, we leave "No messages yet" until
                // real messages exist.
                if (
                  conv.conversation_type === 'pending_invite_outgoing' &&
                  conv.status === 'pending'
                ) {
                  preview = 'Chat Request Pending';
                } else if (
                  conv.conversation_type === 'pending_invite_incoming' &&
                  conv.status === 'pending'
                ) {
                  preview = 'Incoming chat request';
                } else if (
                  conv.conversation_type === 'declined_dm' ||
                  conv.status === 'declined'
                ) {
                  preview = 'Invitation declined';
                }

                // Map to a "membership"
                let membership =
                  conv.status === 'pending' ? 'invite' : 'join';

                // Try to find an existing list item (matrix or optimistic)
                // for this same peer so we can reuse onSelect + highlighting.
                const peerId = Number(conv.other_user_id || 0);
                const matchingItem = mergedItems.find((it) => {
                  if (!Number.isFinite(peerId)) return false;
                  if (Number.isFinite(it?.peerUid) && Number(it.peerUid) === peerId) {
                    return true;
                  }
                  return false;
                });

                // sort_date is a text timestamp from the backend or synthetic row
                const backendTs = conv.sort_date ? Date.parse(conv.sort_date) : 0;

                // Now it is SAFE to reference matchingItem
                let effectiveTs =
                  matchingItem && Number.isFinite(Number(matchingItem.lastActive))
                    ? Number(matchingItem.lastActive)
                    : backendTs;

                // TEAM ROOMS: build preview from the actual Matrix room timeline (sender + last message)
                if (conv.conversation_type === 'team_room' && conv.room_id && mxClient?.getRoom) {
                  const teamRoom = mxClient.getRoom(conv.room_id);

                  if (teamRoom) {
                    const events =
                      (teamRoom.getLiveTimeline?.().getEvents?.() || []) ||
                      teamRoom.timeline ||
                      [];

                    for (let i = events.length - 1; i >= 0; i--) {
                      const ev = events[i];
                      if (ev?.getType?.() !== 'm.room.message') continue;

                      const c = ev.getContent?.() || {};
                      if (c.msgtype !== 'm.text') continue;

                      let body = (c.body || '').toString().trim();
                      if (!body) continue;

                      // Strip Matrix reply fallback
                      if (body.startsWith('> ')) {
                        const idx = body.indexOf('\n\n');
                        if (idx >= 0) body = body.slice(idx + 2);
                      }

                      const ts = Number(ev.getTs?.() || 0) || 0;
                      if (ts) effectiveTs = ts;

                      const senderId = ev.getSender?.() || '';
                      // Get display name, falling back gracefully (never show raw Matrix ID)
                      const memberName = teamRoom.getMember?.(senderId)?.name || '';
                      const senderName = (memberName && !memberName.startsWith('@'))
                        ? memberName
                        : '';

                      preview = senderName ? `${senderName}: ${body}` : body;

                      break;
                    }
                  }
                }

                const roomId = (matchingItem && matchingItem.id) ? matchingItem.id : (conv.room_id || null);
                const unreadCount = roomId ? (unreadCounts && unreadCounts[roomId] ? unreadCounts[roomId] : 0) : 0;

                // If we already have a joined Matrix DM room for this peer,
                // treat the conversation as a fully connected chat:
                //  - membership becomes "join" (gray text)
                //  - preview comes from the live Matrix room timeline
                if (matchingItem && matchingItem.membership === 'join') {
                  membership = 'join';

                  if (matchingItem.preview && String(matchingItem.preview).trim()) {
                    preview = matchingItem.preview;
                  }
                }

                // For any DM with a room_id, try reading last message directly
                // from the Matrix room timeline. Covers two cases:
                // 1) matchingItem exists but preview was empty (items memo race)
                // 2) No matchingItem (isStrictDM() filtered room out of items)
                if (
                  preview === 'No messages yet' &&
                  conv.room_id &&
                  mxClient?.getRoom &&
                  (conv.conversation_type === 'active_matrix_dm' || conv.conversation_type === 'accepted_dm')
                ) {
                  const dmRoom = mxClient.getRoom(conv.room_id);
                  if (dmRoom) {
                    membership = 'join';
                    const lt = lastText(dmRoom);
                    if (lt.text) {
                      preview = lt.text;
                      if (lt.ts && lt.ts > effectiveTs) effectiveTs = lt.ts;
                    }
                  }
                }

                // Compute previewWhen AFTER enrichment so updated effectiveTs is used
                const previewWhen = tinyWhen(effectiveTs);

                  const isTeamInvitePreview =
                    typeof preview === 'string' &&
                    preview.toLowerCase().includes('invitation has been sent');
                  const isActive = matchingItem

                  ? (
                      String(selectedThread || '') === String(matchingItem.joinable) ||
                      String(selectedThread || '') === String(matchingItem.id)
                    )
                  : (
                      (conv.conversation_type === 'active_matrix_dm' || conv.conversation_type === 'team_room') &&
                      conv.room_id &&
                      String(selectedThread || '') === String(conv.room_id)
                    );


                const formatInviteTerminalMessage = (terminalReason) => {
                  if (!terminalReason) {
                    return 'You have already responded to this team invitation.';
                  }

                  const normalized = terminalReason.toLowerCase();

                  if (normalized.includes('accepted')) {
                    return 'You accepted this team invitation.';
                  }

                  if (normalized.includes('declined')) {
                    return 'You declined this team invitation.';
                  }

                  if (normalized.includes('expired')) {
                    return 'This team invitation has expired. Please contact the team captain to request a new invitation.';
                  }

                  return 'You have already responded to this team invitation.';
                };

                  const handleClick = () => {




                    if (matchingItem) {
                      onSelect(matchingItem);
                    } else if (
                      (conv.conversation_type === 'active_matrix_dm' || conv.conversation_type === 'team_room') &&
                      conv.room_id
                    ) {
                      onSelect({
                        id: conv.room_id,
                        joinable: conv.room_id,
                        alias: null,
                        membership: 'join',
                        title,
                        avatarUrl,
                        preview,
                        previewWhen,
                        lastActive: effectiveTs,
                        peerUid: conv.conversation_type === 'team_room' ? null : peerId,
                      });
                    } else if (
                      conv.conversation_type === 'accepted_dm' &&
                      !conv.room_id &&
                      Number.isFinite(peerId) && peerId > 0
                    ) {
                      // Accepted DM but room was never created (interrupted).
                      // Create an optimistic thread so ChatView can display it,
                      // then trigger room creation in the background.
                      const myUid = Number(viewerUId || 0);
                      const a = Math.min(myUid, peerId);
                      const b = Math.max(myUid, peerId);
                      const alias = `#eh.dm.u${a}_u${b}:ehandshake.com`;

                      // Ensure an optimistic thread exists for this alias
                      const mStore = useMessagingStore.getState();
                      const alreadyExists = (mStore.optimisticThreads || []).some(
                        t => String(t.alias) === alias
                      );
                      if (!alreadyExists) {
                        mStore.addOptimisticDM({
                          alias,
                          peerUid: peerId,
                          displayName: title || null,
                          avatarUrl: avatarUrl || null,
                          lastMessagePreview: 'Setting up your private chat…',
                          lastMessageAt: effectiveTs || Date.now(),
                          direction: 'outgoing',
                          inviteSent: true,
                          status: 'accepted',
                        });
                      }

                      onSelect({
                        id: `opt:${alias}`,
                        joinable: alias,
                        alias,
                        membership: 'optimistic',
                        title,
                        avatarUrl,
                        preview,
                        previewWhen,
                        lastActive: effectiveTs,
                        peerUid: peerId,
                      });

                      // Re-trigger room creation for this accepted-but-missing room

                      fetch('/api/matrix/create-dm', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ candidateUserId: peerId }),
                      })
                        .then(r => r.json().catch(() => null))
                        .then(data => {
                          if (data?.success && data?.roomId) {

                            setSelectedThread(data.roomId);
                            setSelectedRoomId(data.roomId);
                          } else {
                            console.warn('[ChatList] create-dm recovery failed:', data);
                          }
                        })
                        .catch(err => console.error('[ChatList] create-dm recovery error:', err));

                    } else if (
                      (conv.conversation_type === 'pending_invite_outgoing' ||
                       conv.conversation_type === 'pending_invite_incoming' ||
                       conv.conversation_type === 'declined_dm') &&
                      Number.isFinite(peerId) && peerId > 0
                    ) {
                      // Pending/declined invites with no Matrix room yet —
                      // construct the canonical alias and select as optimistic
                      const myUid = Number(viewerUId || 0);
                      const a = Math.min(myUid, peerId);
                      const b = Math.max(myUid, peerId);
                      const alias = `#eh.dm.u${a}_u${b}:ehandshake.com`;
                      onSelect({
                        id: `opt:${alias}`,
                        joinable: alias,
                        alias,
                        membership: 'optimistic',
                        title,
                        avatarUrl,
                        preview,
                        previewWhen,
                        lastActive: effectiveTs,
                        peerUid: peerId,
                      });
                    }
                  };



                return (
                  <li key={conv.id}>
                    <button
                      type="button"
                      onClick={handleClick}
                      className={
                        'w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition ' +
                        (isActive
                          ? 'bg-[#E9F8FF] border border-[#00aef7]'
                          : 'hover:bg-gray-50 border border-transparent')
                      }
                      title={title}
                    >
                      <PresenceAvatar
                        src={avatarUrl}
                        userId={conv.conversation_type !== 'team_room' ? conv.other_user_id : null}
                        size={28}
                        fallback={initialsOf(title)}
                        isTeam={conv.conversation_type === 'team_room'}
                      />


                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-xs font-medium text-gray-900">
                            {title}
                          </div>

                          {unreadCount > 0 ? (
                            <span className="shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#00aef7] text-white text-[10px] font-semibold">
                              <svg
                              viewBox="0 0 24 24"
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            >
                              <text
                                x="12"
                                y="15"
                                textAnchor="middle"
                                fontSize="9"
                                fontWeight="700"
                                fill="currentColor"
                                fontFamily="system-ui, -apple-system, Segoe UI, Roboto, Arial"
                              >
                                NEW
                              </text>
                            </svg>

                            </span>
                          ) : null}
                          {previewWhen ? (
                            <span className="ml-auto shrink-0 text-[10px] leading-4 text-gray-400">
                              {previewWhen}
                            </span>
                          ) : null}
                        </div>

                        <div
                          className={
                            'text-[11px] leading-4 truncate ' +
                            ((membership === 'invite' || isTeamInvitePreview)
                              ? 'font-semibold text-green-700'
                              : 'text-gray-500')
                          }
                        >
                          {preview}
                        </div>

                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}



        {/* === Unified conversations === */}

        {!sortedConversations.length ? (
          <ul className="space-y-1">
            <li className="p-2 rounded-md border border-dashed border-gray-200 text-gray-400 text-sm">
              (no conversations yet)
            </li>
          </ul>
        ) : null}


        {archivedItems.length > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <button
              type="button"
              onClick={() => setShowArchived(prev => !prev)}
              className="w-full flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px]"
            >
              <span className="font-medium text-gray-600">
                Declined conversations ({archivedItems.length})
              </span>
              <span className="text-[10px] text-gray-500">
                {showArchived ? 'Hide' : 'Show'}
              </span>
            </button>

            {showArchived && (
              <ul className="mt-2 space-y-1">
                {archivedItems.map((it) => (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(it)}
                      className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition hover:bg-gray-50 border border-transparent"
                      title={it.title}
                    >
                      <PresenceAvatar
                        src={it.avatarUrl}
                        userId={it.peerUid}
                        size={28}
                        fallback={initialsOf(it.title)}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-xs font-medium text-gray-900">{it.title}</div>
                          {it.previewWhen ? (
                            <span className="ml-auto shrink-0 text-[10px] leading-4 text-gray-400">
                              {it.previewWhen}
                            </span>
                          ) : null}
                        </div>
                        <div
                          className={
                            "text-[11px] leading-4 truncate " +
                            (it.previewTone === "teamInvite"
                              ? "font-semibold text-green-700"
                              : "text-gray-500")
                          }
                        >
                          {it.preview}
                        </div>

                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
